import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { AddGreeting } from '../../../../app.interface'
import { localUserQuestionUpdate } from '@/store/questions/actions'
import { getIntent } from '@/store/questions/selector'
import { isHtmlContentEmpty } from '@/utils/markdownConverter'
import GreetingsWithGroups from '@/components/UI/GreetingsWithGroups/GreetingsWithGroups'
import PermissionWrapper from '@/components/UI/PermissionWrapper/PermissionWrapper'
import { BrainlyMathEditorSimple } from '@/components/UI/TextAreaNewVersion'

const VirtualAssistantTab = (props: any) => {
  const { t } = useTranslation('translation')
  const dispatch = useDispatch()
  const intent = useSelector(getIntent)
  const [addVirtualAssistantAnswer, setVirtualAssistantAnswer] = useState({
    text: '',
    group_ids: [],
  })
  const [virtualAssistantList, setVirtualAssistantList] = useState([])
  const [isAdding, setIsAdding] = useState(false)
  const [editorKey, setEditorKey] = useState(0) // Key to force editor remount

  // Helper function to validate content (including markdown with LaTeX math)
  const hasValidContent = (content: string): boolean => {
    // Use the imported helper that properly detects LaTeX math expressions
    return !isHtmlContentEmpty(content)
  }

  useEffect(() => {
    // Filter out empty responses from backend data and add local IDs if missing
    const responses = (intent && intent.responses) || []
    const validResponses = responses
      .filter((r: any) => r && r.text && hasValidContent(r.text))
      .map((r: any, idx: number) => ({
        ...r,
        _localId: r._localId || r._id || `greeting_${idx}_${Date.now()}`,
      }))
    setVirtualAssistantList(validResponses)
  }, [intent])

  useEffect(() => {
    if (props.hasAdded) {
      setVirtualAssistantAnswer({ text: '', group_ids: [] })
    }
  }, [props.hasAdded])

  // const onVirtualAssistantEditorChange = (id: any, name: string) => {
  //   addVirtualAssistantAnswer.text = name
  //   setVirtualAssistantAnswer(addVirtualAssistantAnswer)
  //   props.isActivityDetected &&
  //     props.isActivityDetected(addVirtualAssistantAnswer)
  // }

  const handleVirtualAssistantAnswerChange = (htmlValue: string) => {
    const updatedAnswer = {
      ...addVirtualAssistantAnswer,
      text: htmlValue,
    }
    setVirtualAssistantAnswer(updatedAnswer)
    props.isActivityDetected && props.isActivityDetected(updatedAnswer)
  }

  ///////// Editor handlers ( LOOP )  ///////////
  const addVirtualAssistantHandler = () => {
    // Prevent double-click
    if (isAdding) {
      return
    }

    // Validate content using helper function
    const htmlContent = addVirtualAssistantAnswer?.text || ''

    if (!hasValidContent(htmlContent)) {
      return // Don't add empty variants or variants with only HTML tags
    }

    setIsAdding(true)

    const oldList = [...virtualAssistantList]
    oldList.unshift({
      text: htmlContent.trim(),
      group_ids: [],
      _localId: `greeting_${Date.now()}_${Math.random()}`,
    })
    setVirtualAssistantList([...oldList])
    dispatch(localUserQuestionUpdate('responses', oldList))

    // Clear the editor by resetting the state and incrementing key
    setVirtualAssistantAnswer({ text: '', group_ids: [] })
    setEditorKey(prev => prev + 1) // Force editor re-mount
    props.isActivityDetected && props.isActivityDetected(null)

    // Reset isAdding after a short delay
    setTimeout(() => {
      setIsAdding(false)
    }, 500)
  }

  const onUpdateGreetingsHandler = (greetings: AddGreeting[]) => {
    setVirtualAssistantList([...greetings])
    dispatch(localUserQuestionUpdate('responses', [...greetings]))
  }

  const onCopyExistingGreetingHandler = async (event: any) => {
    // Validate copied text using helper function
    const htmlContent = event?.text || ''

    if (!hasValidContent(htmlContent)) {
      return // Don't copy empty variants or variants with only HTML tags
    }

    const oldList = [...virtualAssistantList]
    oldList.unshift({
      text: htmlContent.trim(),
      group_ids: event.group_ids || [],
      _localId: `greeting_${Date.now()}_${Math.random()}`,
    })
    setVirtualAssistantList([...oldList])
    dispatch(localUserQuestionUpdate('responses', oldList))
    setVirtualAssistantAnswer({ text: '', group_ids: [] })
  }

  const renderGreetings = (
    <div>
      <GreetingsWithGroups
        greetings={[...virtualAssistantList]}
        allUserGroups={[...props.allUserGroups]}
        onCopyExistingGreeting={onCopyExistingGreetingHandler}
        onUpdateGreetings={onUpdateGreetingsHandler}
      />
    </div>
  )
  return (
    <div>
      <div
        className={
          props.nodeElement || props.createFromScratch
            ? 'user-ask-ques flex-wrap third-step py-0 position-relative col-lg-12 col-xl-12'
            : 'user-ask-ques flex-wrap third-step py-0 position-relative col-lg-9 col-xl-8'
        }
        style={{ position: 'relative' }}
      >
        <BrainlyMathEditorSimple
          key={editorKey}
          value={addVirtualAssistantAnswer?.text || ''}
          onChange={handleVirtualAssistantAnswerChange}
          placeholder={t('Type answer here')}
          height="200px"
          minHeight="150px"
        />
        {/* Add button positioned on top-right of editor toolbar */}
        <div
          style={{
            position: 'absolute',
            top: '10px',
            right: '20px',
            zIndex: 10,
          }}
        >
          <PermissionWrapper>
            <button
              className="btn btn-link"
              onClick={addVirtualAssistantHandler}
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#0066FF',
                textDecoration: 'none',
                padding: '4px 12px',
              }}
            >
              {t('Add')}
            </button>
          </PermissionWrapper>
        </div>
      </div>
      {renderGreetings}
    </div>
  )
}

export default VirtualAssistantTab
